/**
 * Реестр проектов (US-26 FR-3).
 *
 * Строка = **проект**, а не договор (FR-3.1); раскрытие «шеврона»
 * показывает договоры проекта — СМР и надзор, каждый отдельной строкой
 * (FR-3.6); клик по строке вне «шеврона» открывает карточку (FR-4.1).
 * Вкладок «СМР» / «ПСД» над реестром нет: раздел показывает БИП целиком,
 * и экран охват не фильтрует (FR-3.3, FR-1.6).
 *
 * **Фильтры, поиск и страница уходят в запрос** (US-27 FR-1.4): страница
 * реестра не больше 100 строк, и фильтрация «на экране» показывала бы
 * только её. Плашка при этом не меняется — она считается отдельной
 * операцией по всей территории (FR-2.6, FR-3.5). Поиск отправляется с
 * задержкой, чтобы каждая буква не уходила запросом.
 *
 * Экран **не группирует договоры в проекты и не дедуплицирует их**:
 * проект приходит собранным поставкой (FR-3.7).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProjectTypePill,
  StatusPill,
  SupervisionOnlyBadge,
} from './StatusPill';
import { MoneyMln, NoData, useLocalizedText } from './projectsView';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  ProjectsAbpRow,
  ProjectsRegistryData,
  ProjectsRegistryQuery,
} from '../../../types/projects';

const ALL = 'all';

/** Задержка перед отправкой поиска, мс. */
const SEARCH_DEBOUNCE_MS = 350;

interface ProjectsRegistryProps {
  data: ProjectsRegistryData | null;
  loading: boolean;
  query: ProjectsRegistryQuery;
  onQueryChange: (query: ProjectsRegistryQuery) => void;
  /**
   * Заказчики территории для фильтра «АБП» — из ответа виджета АБП: он
   * считается по всей территории без фильтров, поэтому перечень полный, а
   * страница реестра дала бы только заказчиков текущих двадцати строк.
   */
  abpOptions: ProjectsAbpRow[];
  onOpen: (projectUid: string) => void;
}

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  allLabel: string;
  options: Array<{ value: string; label: string }>;
  testId: string;
}

const FilterSelect = ({
  value,
  onChange,
  allLabel,
  options,
  testId,
}: FilterSelectProps) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      className="h-9 w-auto min-w-36 max-w-full gap-2 text-sm"
      data-testid={testId}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{allLabel}</SelectItem>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const ProjectsRegistry = ({
  data,
  loading,
  query,
  onQueryChange,
  abpOptions,
  onOpen,
}: ProjectsRegistryProps) => {
  const { t } = useTranslation();
  const loc = useLocalizedText();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState(query.search);

  // Поле поиска — локальное состояние, в запрос уходит с задержкой.
  useEffect(() => {
    setSearch(query.search);
  }, [query.search]);

  useEffect(() => {
    if (search === query.search) return;
    const timer = setTimeout(() => {
      onQueryChange({ ...query, search, page: 1 });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `onQueryChange` и `query` стабильны между наборами букв.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // FR-9.1: новая выдача (смена территории, фильтра, страницы) сбрасывает
  // раскрытые строки — раскрытого проекта на новой странице может не быть.
  useEffect(() => {
    setExpanded(new Set());
  }, [data]);

  const items = data?.items ?? [];

  /**
   * Значения фильтра «Статус» — перечень `statuses` выдачи (US-27
   * FR-5.1.1): он считается по всей территории, а страница дала бы только
   * статусы текущих двадцати строк. Номенклатуру задаёт источник (DR-03),
   * поэтому зашитого списка нет. Выбранное значение остаётся в списке
   * всегда — даже если к следующей загрузке оно из данных пропало, фильтр
   * не должен молча показывать «все».
   */
  const statusOptions = [
    ...new Set([
      ...(data?.statuses ?? []),
      ...(query.status !== null ? [query.status] : []),
    ]),
  ];

  const abpSelectOptions = abpOptions.flatMap((row) => {
    const value = row.abpCode;
    if (value === null) return [];
    return [{ value, label: loc(row.abpName) ?? value }];
  });

  const toggleExpanded = (projectUid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(projectUid)) next.delete(projectUid);
      else next.add(projectUid);
      return next;
    });
  };

  const setFilter = (patch: Partial<ProjectsRegistryQuery>) => {
    // Смена фильтра всегда возвращает на первую страницу: иначе после
    // сужения выборки экран остался бы на несуществующей странице.
    onQueryChange({ ...query, ...patch, page: 1 });
  };

  const pageSize = data?.pageSize ?? 0;
  const pageCount =
    data === null || pageSize === 0
      ? 1
      : Math.max(1, Math.ceil(data.totalFiltered / pageSize));
  const page = data?.page ?? query.page;
  const reportYear = data?.reportYear;

  return (
    <Card className="shadow-card" data-testid="block-projects-registry">
      <CardHeader className="flex-row flex-wrap items-baseline gap-x-3 gap-y-1 space-y-0">
        <CardTitle>{t('projects.registry.title')}</CardTitle>
        <span className="text-xs text-text-faint">
          {t('projects.registry.hint')}
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="flex flex-wrap gap-2"
          data-testid="projects-registry-filters"
        >
          <FilterSelect
            value={query.status ?? ALL}
            onChange={(value) =>
              setFilter({ status: value === ALL ? null : value })
            }
            allLabel={t('projects.registry.statusAll')}
            // Подпись — значение источника как есть: ключом локали оно
            // быть не может (точка в «доп.соглашение» — разделитель ключа),
            // а свой словарь переводов — та же нормализация (DR-03, O2).
            options={statusOptions.map((value) => ({ value, label: value }))}
            testId="projects-filter-status"
          />
          <FilterSelect
            value={query.abpCode ?? ALL}
            onChange={(value) =>
              setFilter({ abpCode: value === ALL ? null : value })
            }
            allLabel={t('projects.registry.abpAll')}
            options={abpSelectOptions}
            testId="projects-filter-abp"
          />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('projects.registry.searchPlaceholder')}
            className="h-9 min-w-44 flex-1 text-sm"
            data-testid="projects-registry-search"
          />
        </div>

        {loading ? (
          <BlockLoader />
        ) : items.length === 0 ? (
          // FR-3.10: пустое состояние — явная подпись, не пустая рамка.
          <p
            className="py-8 text-center text-sm text-text-muted"
            data-testid="projects-registry-empty"
          >
            {t('projects.registry.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[60rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>{t('projects.registry.name')}</TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.registry.totalCost')}
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.registry.spentTotal')}
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.registry.remainder')}
                  </TableHead>
                  <TableHead>{t('projects.registry.abp')}</TableHead>
                  <TableHead>{t('projects.registry.status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => {
                  const isOpen = expanded.has(row.projectUid);
                  const abpLabel = loc(row.abpName) ?? row.abpCode;
                  const workKindLabel =
                    row.workKind === null
                      ? null
                      : t(`projects.registry.workKinds.${row.workKind}`, {
                          defaultValue: row.workKind,
                        });
                  return [
                    <TableRow
                      key={row.projectUid}
                      tabIndex={0}
                      className="cursor-pointer hover:bg-brand-50/60 focus-visible:bg-brand-50/60"
                      onClick={() => onOpen(row.projectUid)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onOpen(row.projectUid);
                      }}
                      data-testid={`projects-registry-row-${row.projectUid}`}
                    >
                      <TableCell className="py-2 pr-0">
                        {row.contracts.length > 0 && (
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-label={t('projects.registry.toggleContracts')}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(row.projectUid);
                            }}
                            className="rounded p-1 text-text-faint hover:bg-surface-muted hover:text-text"
                            data-testid={`projects-registry-toggle-${row.projectUid}`}
                          >
                            <ChevronRight
                              className={cn(
                                'size-4 transition-transform',
                                isOpen && 'rotate-90',
                              )}
                            />
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="min-w-0 max-w-[26rem]">
                        <span className="font-semibold leading-snug">
                          {loc(row.name)}
                        </span>
                        {row.supervisionOnly && <SupervisionOnlyBadge />}
                        <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-text-muted">
                          {[loc(row.katoName), workKindLabel]
                            .filter(Boolean)
                            .join(' · ')}
                          <ProjectTypePill type={row.projectType} />
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        <MoneyMln value={row.money.totalCost} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <MoneyMln value={row.money.spentTotal} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <MoneyMln value={row.money.remainder} />
                      </TableCell>
                      <TableCell
                        className="max-w-56 text-text-muted"
                        title={abpLabel ?? undefined}
                      >
                        <span className="line-clamp-2">
                          {abpLabel ?? <NoData />}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusPill status={row.status} />
                      </TableCell>
                    </TableRow>,
                    // FR-3.6: раскрытие — договоры проекта, каждый своей
                    // строкой (договоров надзора у проекта бывает несколько).
                    ...(isOpen
                      ? row.contracts.map((contract, index) => (
                          <TableRow
                            key={`${row.projectUid}-${contract.contractNumber ?? index}`}
                            className="bg-surface-page/70 hover:bg-surface-page/70"
                            data-testid={`projects-registry-contract-${row.projectUid}-${index}`}
                          >
                            <TableCell />
                            <TableCell className="min-w-0 max-w-[26rem]">
                              <span className="flex flex-wrap items-baseline gap-1.5">
                                <span
                                  className={cn(
                                    'rounded px-1.5 py-0.5 text-xs font-semibold',
                                    contract.kind === 'SMR'
                                      ? 'bg-brand-50 text-brand-700'
                                      : 'bg-surface-muted text-text-muted',
                                  )}
                                >
                                  {t(
                                    `projects.registry.contractKinds.${contract.kind}`,
                                    { defaultValue: contract.kind },
                                  )}
                                </span>
                                <span className="min-w-0 text-sm">
                                  {loc(contract.name)}
                                </span>
                              </span>
                              <span className="mt-0.5 block text-xs text-text-muted">
                                {[
                                  contract.contractNumber === null
                                    ? null
                                    : t('projects.registry.contractNumber', {
                                        number: contract.contractNumber,
                                      }),
                                  contract.contractorName,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <MoneyMln value={contract.money.totalCost} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <MoneyMln value={contract.money.spentTotal} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <MoneyMln value={contract.money.remainder} />
                            </TableCell>
                            <TableCell />
                            <TableCell>
                              <StatusPill status={contract.status} />
                            </TableCell>
                          </TableRow>
                        ))
                      : []),
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-surface-border pt-2.5">
          <div className="space-y-1 text-xs text-text-faint">
            <p data-testid="projects-registry-count">
              {t('projects.registry.shown', {
                shown: data?.shown ?? 0,
                total: data?.totalOnTab ?? 0,
              })}
            </p>
            {/* FR-3.2 и FR-3.9: смысл «всего освоено» и статуса — сносками. */}
            <p data-testid="projects-registry-notes">
              {t('projects.registry.noteSpent', { year: reportYear ?? '' })}
              {' · '}
              {t('projects.registry.noteStatus')}
            </p>
          </div>

          {pageCount > 1 && (
            <div
              className="flex items-center gap-2"
              data-testid="projects-registry-pager"
            >
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => onQueryChange({ ...query, page: page - 1 })}
                aria-label={t('projects.registry.pagePrev')}
                data-testid="projects-registry-prev"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="whitespace-nowrap text-xs tabular-nums text-text-muted">
                {t('projects.registry.page', { page, total: pageCount })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount || loading}
                onClick={() => onQueryChange({ ...query, page: page + 1 })}
                aria-label={t('projects.registry.pageNext')}
                data-testid="projects-registry-next"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
